import { z } from "zod"

import { useSafeSessionStorage } from "~/hooks/useSafeLocalStorage"

const SalesLevelSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("total") }),
  z.object({ kind: z.literal("channel"), id: z.string() }),
  z.object({ kind: z.literal("warehouse"), id: z.string() }),
  z.object({ kind: z.literal("market"), id: z.string() }),
  z.object({ kind: z.literal("customer"), id: z.string(), name: z.string().optional() }),
])
export type SalesLevel = z.infer<typeof SalesLevelSchema>

const VariantSetSchema = z.object({
  added: z.array(z.string()),
  removed: z.array(z.string()),
})
type VariantSet = z.infer<typeof VariantSetSchema>

const DraftAdjustmentSchema = z.object({
  id: z.string(),
  // Rows written by one save share a groupId — a collection-wide adjustment under a group scope is
  // one row per member collection.
  groupId: z.string().nullable().optional(),
  variantId: z.string(),
  level: SalesLevelSchema,
  // Full composite scope columns (sentinel "" = "all"). `level` collapses this to a single
  // most-specific axis for writes; `scope` keeps every set axis so the hover can show them all.
  scope: z
    .object({ channel: z.string(), warehouseId: z.string(), marketId: z.string() })
    .optional(),
  // 'setup' = part of building the demand setup (windowless); 'manual' = an additive per-slice delta
  // inside the Manual value (windowless); 'overlay' = a temporary on-top campaign effect (windowed).
  // Optional on a draft row; the save path defaults to 'overlay'.
  stage: z.enum(["setup", "manual", "overlay"]).optional(),
  // Which band an on-top adjustment moves; defaults to 'sales' (today's behaviour) when absent.
  target: z.enum(["sales", "preorder", "both"]).optional(),
  theme: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  params: z.unknown().optional(),
  filters: z.record(z.string(), z.array(z.string())).optional(),
  type: z.enum(["percentage", "add"]),
  value: z.number(),
  // Active period for windowed (on-top) adjustments; null/absent = applies across the whole horizon.
  startDate: z.date().nullable().optional(),
  endDate: z.date().nullable().optional(),
  seq: z.number(),
  ts: z.number(),
  createdBy: z.string().nullable().optional(),
})
export type DraftAdjustment = z.infer<typeof DraftAdjustmentSchema>

const DraftForecastSchema = z.object({
  total: z.number(),
  sizeSplit: z.record(z.string(), z.number()),
  weekly: z.array(z.number()).length(52).nullable(),
  source: z.enum(["baseline", "manual", "weekly"]),
})
export type DraftForecast = z.infer<typeof DraftForecastSchema>

const SalesLevelDefault: SalesLevel = { kind: "total" }

const variantSetSchema = VariantSetSchema

export const useDraftVariantSet = (collectionId: number) =>
  useSafeSessionStorage<VariantSet>(
    `demand2.${collectionId}.variantSet`,
    { added: [], removed: [] },
    variantSetSchema,
  )

// variantId → the breakdown keys that are ON for it, whichever way each is stored. Applied state persists to
// DemandSetupInsight, not DemandAdjustment; this session draft holds page-wide UNSAVED edits, which the
// Commandbar reconciles.
const noosEnabledInsightsSchema = z.record(z.string(), z.array(z.string()))
export const useNoosEnabledDraft = (collectionId: number) =>
  useSafeSessionStorage<Record<string, string[]>>(
    `demand2.${collectionId}.noosEnabledInsightsDraft.v2`,
    {},
    noosEnabledInsightsSchema,
  )

export const sameStringSet = (a: string[] = [], b: string[] = []): boolean => {
  const sa = new Set(a)
  const sb = new Set(b)
  return sa.size === sb.size && [...sa].every((x) => sb.has(x))
}

export const useSalesLevel = (collectionId: number) =>
  useSafeSessionStorage<SalesLevel>(
    `demand2.${collectionId}.salesLevel`,
    SalesLevelDefault,
    SalesLevelSchema,
  )

const highlightedVariantsSchema = z.array(z.string())
/**
 * Highlighted variant ids — the inspector's input, which the table keeps mirroring its checkbox
 * selection. Persisted to survive reload.
 *
 * Keyed by TAB as well as collection: the tabs list different variants, so one shared slot would
 * carry a highlight onto a tab the planner never picked it on. Each tab restores only its own.
 */
export const useHighlightedVariants = (collectionId: number, tab: HighlightTab) =>
  useSafeSessionStorage<string[]>(
    `demand2.${collectionId}.${tab}.highlightedVariantIds`,
    [],
    highlightedVariantsSchema,
  )

/** The tab a highlight belongs to — the pages that own a table, so `info` is not one of them. */
export type HighlightTab = "assortment" | "demand" | "performance"

export const salesLevelLabel = (level: SalesLevel) => {
  switch (level.kind) {
    case "total":
      return "Total"
    case "channel":
      return `Channel: ${level.id}`
    case "warehouse":
      return `Warehouse: ${level.id}`
    case "market":
      return `Market: ${level.id}`
    case "customer":
      return `Customer: ${level.name ?? level.id}`
  }
}
